import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { makeRequest2 } from './makeRequest';
import { recordCashPayment } from './urls';
import { toastify } from './toast';
import { v4 as uuidv4 } from 'uuid';

const CashPaymentModal = ({
    visible,
    onHide,
    invoice,
    facilityId,
    onSuccess,
    onError
}) => {
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);
    const [formData, setFormData] = useState({
        amount: 0,
        receiptNumber: `CASH-${uuidv4().substring(0, 8)}`,
        paymentMethod: 'Cash',
        paymentDate: new Date().toISOString().split('T')[0],
        notes: '',
        currencyId: '',
        checkNumber: '',
        bankName: '',
        transferReference: ''
    });

    // Kenya Banks List
    const kenyaBanks = [
        'KCB Bank Kenya Limited',
        'Safaricom M-Pesa',
        'Equity Bank Kenya Limited',
        'NCBA Bank Kenya PLC',
        'Co-operative Bank of Kenya Ltd',
        'Absa Bank Kenya PLC',
        'Standard Chartered Bank Kenya Ltd',
        'Diamond Trust Bank Kenya Ltd',
        'Stanbic Bank Kenya Ltd',
        'I&M Bank Ltd',
        'African Banking Corporation Ltd (ABC Bank)',
        'Bank of Africa Kenya Ltd',
        'Bank of Baroda (Kenya) Ltd',
        'Bank of India',
        'Citibank N.A. Kenya',
        'Commercial   Bank Kenya Ltd (CIB)',
        'DIB Bank Kenya Ltd',
        'Ecobank Kenya Ltd',
        'Family Bank Ltd',
        'Guaranty Trust Bank (Kenya) Ltd',
        'Gulf African Bank Ltd',
        'Kingdom Bank Ltd',
        'National Bank of Kenya Ltd',
        'Prime Bank Ltd',
        'SBM Bank Kenya Ltd',
        'Other'
    ];

    useEffect(() => {
        if (invoice) {
            setFormData(prev => ({
                ...prev,
                amount: (invoice.totalAmount || 0) + (invoice.balanceBroughtForward || 0) - (invoice.amountPaid || 0),
                currencyId: invoice?.currency?.id || ''
            }));
        }
    }, [invoice]);

    useEffect(() => {
        const prefix = formData.paymentMethod === 'Check' ? 'CHECK' :
            formData.paymentMethod === 'Bank Transfer' ? 'BANK' : 'CASH';
        setFormData(prev => ({
            ...prev,
            receiptNumber: `${prefix}-${uuidv4().substring(0, 8)}`
        }));
    }, [formData.paymentMethod, visible]);

    useEffect(() => {
        if (visible) {
            setFormData(prev => ({
                ...prev,
                receiptNumber: `CASH-${uuidv4().substring(0, 8)}`,
                paymentDate: new Date().toISOString().split('T')[0],
                notes: '',
                checkNumber: '',
                bankName: '',
                transferReference: ''
            }));
            setFile(null);
        }
    }, [visible]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    // Convert file to base64
    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (!facilityId || !invoice?._id) {
                throw new Error('Missing required payment details');
            }

            if (parseFloat(formData.amount) <= 0) {
                throw new Error('Payment amount must be greater than zero');
            }

            // Prepare payload as JSON
            const payload = {
                invoiceNumber: invoice.invoiceNumber,
                amount: parseFloat(formData.amount),
                receiptNumber: formData.receiptNumber,
                paymentMethod: formData.paymentMethod,
                paymentDate: formData.paymentDate,
                notes: formData.notes,
                currencyId: formData.currencyId || invoice?.currency?.id
            };

            // Add payment method specific fields
            if (formData.paymentMethod === 'Check') {
                payload.checkNumber = formData.checkNumber;
                payload.bankName = formData.bankName;
            } else if (formData.paymentMethod === 'Bank Transfer') {
                payload.transferReference = formData.transferReference;
                payload.bankName = formData.bankName;
            }

            // Convert file to base64 if exists
            if (file) {
                const base64File = await convertToBase64(file);
                payload.fileData = base64File;
                payload.fileName = file.name;
                payload.fileType = file.type;
                payload.fileSize = file.size;
            }

            console.log('Sending payload:', Object.keys(payload));

            // Send as JSON instead of FormData
            const response = await makeRequest2(
                `${recordCashPayment}/${facilityId}`,
                'POST',
                payload,
                false // JSON request instead of multipart
            );

            if (!response.success) {
                throw new Error(response.message || 'Failed to record payment');
            }

            toastify('Payment recorded successfully. Pending approval.', 'success');
            if (onSuccess) onSuccess(response.payment);

            setTimeout(onHide, 2000);
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Payment recording failed. Please try again.';
            toastify(message, 'error');
            if (onError) onError(error);
        } finally {
            setLoading(false);
        }
    };

    const getRemainingBalance = () => {
        return (invoice.totalAmount || 0) + (invoice.balanceBroughtForward || 0) - (invoice?.amountPaid || 0);
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header="Record Invoice Payment"
            modal
            className="p-fluid"
            style={{ width: '450px' }}
            dismissableMask={!loading}
            closeOnEscape={!loading}
        >
            <form onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                    <label htmlFor="amount">Amount ({invoice?.currency?.code || 'KES'})</label>
                    <input
                        type="number"
                        className="form-control"
                        id="amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        disabled={loading}
                        required
                        step="0.01"
                        min="0.01"
                    />
                    <small className="text-muted">
                        Balance due: {invoice?.currency?.code || 'KES'} {getRemainingBalance().toFixed(2)}
                    </small>
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="receiptNumber">Receipt Number</label>
                    <input
                        type="text"
                        className="form-control"
                        id="receiptNumber"
                        name="receiptNumber"
                        value={formData.receiptNumber}
                        onChange={handleInputChange}
                        disabled
                        required
                    />
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="paymentDate">Payment Date</label>
                    <input
                        type="date"
                        className="form-control"
                        id="paymentDate"
                        name="paymentDate"
                        value={formData.paymentDate}
                        onChange={handleInputChange}
                        disabled={loading}
                        required
                        max={new Date().toISOString().split('T')[0]}
                    />
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="paymentMethod">Payment Method</label>
                    <select
                        className="form-select"
                        id="paymentMethod"
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleInputChange}
                        disabled={loading}
                        required
                    >
                        <option value="Cash">Cash</option>
                        <option value="Check">Check</option>
                        <option value="Bank Transfer">Bank/Mpesa</option>
                    </select>
                </div>

                {formData.paymentMethod === 'Check' && (
                    <>
                        <div className="form-group mb-3">
                            <label htmlFor="checkNumber">Check Number</label>
                            <input
                                type="text"
                                className="form-control"
                                id="checkNumber"
                                name="checkNumber"
                                value={formData.checkNumber}
                                onChange={handleInputChange}
                                disabled={loading}
                                required
                            />
                        </div>
                        <div className="form-group mb-3">
                            <label htmlFor="bankName">Bank Name</label>
                            <select
                                className="form-select"
                                id="bankName"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleInputChange}
                                disabled={loading}
                                required
                            >
                                <option value="">Select Bank</option>
                                {kenyaBanks.map((bank, index) => (
                                    <option key={index} value={bank}>
                                        {bank}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                )}

                {formData.paymentMethod === 'Bank Transfer' && (
                    <>
                        <div className="form-group mb-3">
                            <label htmlFor="bankName">Bank/Service Provider</label>
                            <select
                                className="form-select"
                                id="bankName"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleInputChange}
                                disabled={loading}
                                required
                            >
                                <option value="">Select Bank/Service</option>
                                {kenyaBanks.map((bank, index) => (
                                    <option key={index} value={bank}>
                                        {bank}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group mb-3">
                            <label htmlFor="transferReference">
                                {formData.bankName === 'Safaricom M-Pesa' ? 'M-Pesa Transaction Code' : 'Transfer Reference'}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="transferReference"
                                name="transferReference"
                                value={formData.transferReference}
                                onChange={handleInputChange}
                                disabled={loading}
                                required
                                placeholder={formData.bankName === 'Safaricom M-Pesa' ? 'e.g. QH12345678' : 'Transaction reference number'}
                            />
                        </div>
                    </>
                )}

                <div className="form-group mb-3">
                    <label htmlFor="notes">Notes</label>
                    <InputTextarea
                        className="form-control"
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        disabled={loading}
                        rows={3}
                        autoResize={false}
                    />
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="paymentEvidence">Payment Evidence (PDF/Image)</label>
                    <input
                        type="file"
                        className="form-control"
                        id="paymentEvidence"
                        name="paymentEvidence"
                        onChange={handleFileChange}
                        disabled={loading}
                        accept="image/*,.pdf"
                    />
                    <small className="text-muted">
                        Upload receipt or proof of payment (Max 10MB)
                    </small>
                </div>

                <div className="d-flex justify-content-end mt-4">
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        onClick={onHide}
                        className="p-button-text btn btn-secondary me-2"
                        disabled={loading}
                        type="button"
                    />
                    <Button
                        label={loading ? 'Processing...' : 'Record Payment'}
                        icon="pi pi-check"
                        type="submit"
                        disabled={loading}
                        className="btn btn-primary"
                    />
                </div>
            </form>
        </Dialog>
    );
};

export default CashPaymentModal;